import type { ReactNode } from 'react';
import { Button, Skeleton } from './ui';

export function Spinner({ label = 'Loading' }: { label?: string }): JSX.Element {
  return (
    <span className="spinner" role="status" aria-live="polite">
      <span className="spinner__dot" aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

export function LoadingRows({ rows = 3, label }: { rows?: number; label: string }): JSX.Element {
  return (
    <div className="loading-rows" role="status" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="loading-rows__row" />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="empty-state">
      <p className="empty-state__title">{title}</p>
      {children ? <div className="empty-state__body">{children}</div> : null}
      {action ? <div className="empty-state__action">{action}</div> : null}
    </div>
  );
}

/**
 * Errors are shown in full. The server's messages are the product's error
 * surface; truncating them to a generic string would throw away the only part
 * that tells an agent (or a person) what to do next.
 */
export function ErrorNotice({
  error,
  onRetry,
  title,
}: {
  error: Error | null;
  onRetry?: () => void;
  title?: string;
}): JSX.Element | null {
  if (!error) return null;
  return (
    <div className="notice notice--error" role="alert">
      <div>
        <p className="notice__title">{title ?? error.name ?? 'Request failed'}</p>
        <p className="notice__message">{error.message}</p>
      </div>
      {onRetry ? (
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function InlineNotice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'success';
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`notice notice--${tone}`}>{children}</div>
  );
}

export function SectionTitle({
  children,
  actions,
}: {
  children: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="section-title">
      <h2>{children}</h2>
      {actions ? <div className="section-title__actions">{actions}</div> : null}
    </div>
  );
}